import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
export const API_BASE = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';
export const IS_BACKEND_CONFIGURED = Boolean(BACKEND_URL);

if (!IS_BACKEND_CONFIGURED && typeof window !== 'undefined') {
  // eslint-disable-next-line no-console
  console.error(
    '[LoyerBook] REACT_APP_BACKEND_URL is missing. API calls will fail. ' +
    'Set this environment variable on your host (e.g. Netlify → Site settings → Environment variables) and redeploy.'
  );
}

export const api = axios.create({ baseURL: API_BASE });

// -- Token helpers --
function getAccess() { return localStorage.getItem('lb_token') || ''; }
function getRefresh() { return localStorage.getItem('lb_refresh') || ''; }
function saveTokens(access, refresh) {
  if (access) localStorage.setItem('lb_token', access);
  if (refresh) localStorage.setItem('lb_refresh', refresh);
}
function clearTokens() {
  localStorage.removeItem('lb_token');
  localStorage.removeItem('lb_refresh');
  localStorage.removeItem('lb_user');
}

api.interceptors.request.use((config) => {
  const token = getAccess();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

let refreshing = null;

async function tryRefresh() {
  const rt = getRefresh();
  if (!rt) throw new Error('no_refresh');
  if (!refreshing) {
    refreshing = axios
      .post(`${API_BASE}/auth/refresh`, { refresh_token: rt })
      .then((r) => {
        saveTokens(r.data.access_token, r.data.refresh_token);
        return r.data.access_token;
      })
      .finally(() => { refreshing = null; });
  }
  return refreshing;
}

const NO_REFRESH_URLS = ['/auth/refresh', '/auth/login', '/auth/logout', '/auth/mfa'];

function shouldSkipRefresh(url) {
  if (!url) return false;
  return NO_REFRESH_URLS.some((p) => url.includes(p));
}

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const original = err?.config || {};
    if (err?.response?.status === 401 && !original._retry && !shouldSkipRefresh(original.url)) {
      original._retry = true;
      try {
        const newToken = await tryRefresh();
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${newToken}`;
        return api.request(original);
      } catch {
        clearTokens();
        if (!window.location.pathname.startsWith('/login') && !window.location.pathname.startsWith('/connexion')) {
          window.location.href = '/';
        }
      }
    }
    return Promise.reject(err);
  }
);

const FALLBACK_OFFERS = [
  {
    code: 'loyerbook',
    nom: 'LoyerBook',
    locataires_max: 2000000000,
    prix_mensuel_cdf: 3000,
    prix_local: 3000,
    prix_local_approx: 3000,
    devise: 'CDF',
    devise_locale: 'CDF',
    billing_period: 'monthly',
    unlimited_locataires: true,
    description: 'Abonnement mensuel unique avec locataires illimités.',
    actif: true,
    ordre: 10,
  },
];

// Public fallback used only when the catalogue API is temporarily unreachable.
// It mirrors the international footprint displayed by ScanFood so the landing
// still communicates every supported/configurable market. Payment availability
// remains backend-authoritative: no fallback country is marked payable here.
const FALLBACK_COUNTRIES = [
  ['CD', 'RD Congo', '🇨🇩', '+243', 'CDF'],
  ['CI', "Côte d'Ivoire", '🇨🇮', '+225', 'XOF'],
  ['SN', 'Sénégal', '🇸🇳', '+221', 'XOF'],
  ['CM', 'Cameroun', '🇨🇲', '+237', 'XAF'],
  ['BJ', 'Bénin', '🇧🇯', '+229', 'XOF'],
  ['TG', 'Togo', '🇹🇬', '+228', 'XOF'],
  ['ML', 'Mali', '🇲🇱', '+223', 'XOF'],
  ['BF', 'Burkina Faso', '🇧🇫', '+226', 'XOF'],
  ['GA', 'Gabon', '🇬🇦', '+241', 'XAF'],
  ['CG', 'Congo-Brazzaville', '🇨🇬', '+242', 'XAF'],
  ['RW', 'Rwanda', '🇷🇼', '+250', 'RWF'],
  ['KE', 'Kenya', '🇰🇪', '+254', 'KES'],
  ['NG', 'Nigeria', '🇳🇬', '+234', 'NGN'],
  ['GH', 'Ghana', '🇬🇭', '+233', 'GHS'],
  ['MA', 'Maroc', '🇲🇦', '+212', 'MAD'],
  ['TN', 'Tunisie', '🇹🇳', '+216', 'TND'],
  ['DZ', 'Algérie', '🇩🇿', '+213', 'DZD'],
  ['EG', 'Égypte', '🇪🇬', '+20', 'EGP'],
  ['ET', 'Éthiopie', '🇪🇹', '+251', 'ETB'],
  ['TZ', 'Tanzanie', '🇹🇿', '+255', 'TZS'],
  ['UG', 'Ouganda', '🇺🇬', '+256', 'UGX'],
  ['ZA', 'Afrique du Sud', '🇿🇦', '+27', 'ZAR'],
  ['MG', 'Madagascar', '🇲🇬', '+261', 'MGA'],
  ['MU', 'Maurice', '🇲🇺', '+230', 'MUR'],
  ['GN', 'Guinée', '🇬🇳', '+224', 'GNF'],
  ['MZ', 'Mozambique', '🇲🇿', '+258', 'MZN'],
  ['AO', 'Angola', '🇦🇴', '+244', 'AOA'],
  ['ZM', 'Zambie', '🇿🇲', '+260', 'ZMW'],
  ['ZW', 'Zimbabwe', '🇿🇼', '+263', 'USD'],
  ['FR', 'France', '🇫🇷', '+33', 'EUR'],
  ['BE', 'Belgique', '🇧🇪', '+32', 'EUR'],
  ['CH', 'Suisse', '🇨🇭', '+41', 'CHF'],
  ['LU', 'Luxembourg', '🇱🇺', '+352', 'EUR'],
  ['GB', 'Royaume-Uni', '🇬🇧', '+44', 'GBP'],
  ['DE', 'Allemagne', '🇩🇪', '+49', 'EUR'],
  ['ES', 'Espagne', '🇪🇸', '+34', 'EUR'],
  ['IT', 'Italie', '🇮🇹', '+39', 'EUR'],
  ['PT', 'Portugal', '🇵🇹', '+351', 'EUR'],
  ['NL', 'Pays-Bas', '🇳🇱', '+31', 'EUR'],
  ['PL', 'Pologne', '🇵🇱', '+48', 'PLN'],
  ['SE', 'Suède', '🇸🇪', '+46', 'SEK'],
  ['NO', 'Norvège', '🇳🇴', '+47', 'NOK'],
  ['DK', 'Danemark', '🇩🇰', '+45', 'DKK'],
  ['US', 'États-Unis', '🇺🇸', '+1', 'USD'],
  ['CA', 'Canada', '🇨🇦', '+1', 'CAD'],
  ['BR', 'Brésil', '🇧🇷', '+55', 'BRL'],
  ['MX', 'Mexique', '🇲🇽', '+52', 'MXN'],
  ['AR', 'Argentine', '🇦🇷', '+54', 'ARS'],
  ['CO', 'Colombie', '🇨🇴', '+57', 'COP'],
  ['HT', 'Haïti', '🇭🇹', '+509', 'HTG'],
  ['AE', 'Émirats arabes unis', '🇦🇪', '+971', 'AED'],
  ['SA', 'Arabie saoudite', '🇸🇦', '+966', 'SAR'],
  ['IN', 'Inde', '🇮🇳', '+91', 'INR'],
  ['CN', 'Chine', '🇨🇳', '+86', 'CNY'],
  ['JP', 'Japon', '🇯🇵', '+81', 'JPY'],
  ['SG', 'Singapour', '🇸🇬', '+65', 'SGD'],
  ['QA', 'Qatar', '🇶🇦', '+974', 'QAR'],
].map(([code, nom, flag, indicatif, devise_defaut]) => ({
  code,
  nom,
  flag,
  indicatif,
  devise_defaut,
  pricing: { country: code, amount: null, currency: devise_defaut, enabled: false, period: 'monthly' },
  paiement_actif: false,
  moyens_paiement: [],
}));

async function catalogRequest(request, fallbackItems, label) {
  try {
    const response = await request;
    const items = Array.isArray(response?.data?.items) ? response.data.items : [];
    return {
      ...response,
      data: {
        ...response.data,
        items: items.length > 0 ? items : fallbackItems,
        catalog_fallback: items.length === 0,
      },
    };
  } catch (error) {
    // Keep public signup readable when the API/catalogue is temporarily unavailable.
    // The backend remains authoritative for payment amount and account creation.
    // eslint-disable-next-line no-console
    console.warn(`[LoyerBook] Catalogue ${label} indisponible, valeurs par défaut utilisées.`, error);
    return {
      data: {
        items: fallbackItems,
        provider: 'saspay',
        saspay_enabled: false,
        catalog_fallback: true,
      },
    };
  }
}

export const publicApi = {
  offers: (pays) => catalogRequest(
    api.get('/v1/offres', { params: pays ? { pays } : {} }),
    FALLBACK_OFFERS,
    'offres'
  ),
  countries: () => catalogRequest(api.get('/v1/pays'), FALLBACK_COUNTRIES, 'pays'),
  paymentConfiguration: () => api.get('/v1/paiement/configuration'),
  signup: (payload) => api.post('/v1/auth/inscription', payload),
  paymentSync: (intent_id) => api.post('/v1/paiement/sync', { intent_id }),
};

export const authApi = {
  login: (identifier, password, expected_role) => api.post('/auth/login', { identifier, password, expected_role }),
  mfaVerify: (challenge_token, code) => api.post('/auth/mfa/verify', { challenge_token, code }),
  mfaSetup: () => api.post('/auth/mfa/setup'),
  mfaActivate: (code) => api.post('/auth/mfa/activate', { code }),
  refresh: (refresh_token) => api.post('/auth/refresh', { refresh_token }),
  logoutServer: (refresh_token) => api.post('/auth/logout', { refresh_token }),
  me: () => api.get('/auth/me'),
  changePassword: (current_password, new_password) =>
    api.post('/auth/change-password', { current_password, new_password }),
  verifyPassword: (password) => api.post('/auth/verify-password', { password }),
};

export const notifApi = {
  list: (limit = 20) => api.get('/notifications', { params: { limit } }),
  markRead: (id) => api.post(`/notifications/${id}/read`),
  markAllRead: () => api.post('/notifications/read-all'),
};

export const adminApi = {
  kpis: () => api.get('/admin/kpis'),
  financial: () => api.get('/admin/financial'),
  listBailleurs: (status_filter) =>
    api.get('/admin/bailleurs', { params: status_filter ? { status_filter } : {} }),
  createBailleur: (payload) => api.post('/admin/bailleurs', payload),
  getBailleur: (id) => api.get(`/admin/bailleurs/${id}`),
  toggleBailleur: (id, is_active) =>
    api.patch(`/admin/bailleurs/${id}/status`, null, { params: { is_active } }),
  deleteBailleur: (id) => api.delete(`/admin/bailleurs/${id}`),
  updateSubscription: (id, payload) => api.patch(`/admin/bailleurs/${id}/subscription`, payload),
  listLocataires: () => api.get('/admin/locataires'),
  deleteLocataire: (id) => api.delete(`/admin/locataires/${id}`),
  offers: () => api.get('/admin/offers'),
  catalog: () => api.get('/v1/admin/catalogue'),
  updateOffer: (code, payload) => api.patch(`/v1/admin/offres/${code}`, payload),
  updateCountry: (code, payload) => api.patch(`/v1/admin/pays/${code}`, payload),
  updateCurrency: (code, payload) => api.patch(`/v1/admin/devises/${code}`, payload),
  listAgents: () => api.get('/admin/agents'),
  getAgent: (id) => api.get(`/admin/agents/${id}`),
  createAgent: (payload) => api.post('/admin/agents', payload),
  toggleAgent: (id, is_active) =>
    api.patch(`/admin/agents/${id}/status`, null, { params: { is_active } }),
  deleteAgent: (id) => api.delete(`/admin/agents/${id}`),
  audit: (params = {}) => api.get('/admin/audit', { params }),
  anonymize: (user_id) => api.post(`/admin/anonymize/${user_id}`),
};

export const agentApi = {
  dashboard: () => api.get('/agent/dashboard'),
  profile: () => api.get('/v1/agent/profil'),
  listBailleurs: () => api.get('/agent/bailleurs'),
  createBailleur: (payload) => api.post('/agent/bailleurs', payload),
  getBailleur: (id) => api.get(`/agent/bailleurs/${id}`),
  updateSubscription: (id, payload) => api.patch(`/agent/bailleurs/${id}/subscription`, payload),
};

export const bailleurApi = {
  dashboard: () => api.get('/bailleur/dashboard'),
  listLocataires: () => api.get('/bailleur/locataires'),
  createLocataire: (payload) => api.post('/bailleur/locataires', payload),
  getLocataire: (id) => api.get(`/bailleur/locataires/${id}`),
  updateLocataire: (id, payload) => api.patch(`/bailleur/locataires/${id}`, payload),
  deleteLocataire: (id) => api.delete(`/bailleur/locataires/${id}`),
  createPaiement: (payload) => api.post('/bailleur/paiements', payload),
  listPaiements: () => api.get('/bailleur/paiements'),
  createFactureService: (payload) => api.post('/bailleur/factures-services', payload),
  listFacturesServices: () => api.get('/bailleur/factures-services'),
  updateFactureServiceStatus: (id, statut) =>
    api.patch(`/bailleur/factures-services/${id}/statut`, { statut }),
  recuUrl: (paiement_id) => `${API_BASE}/bailleur/paiements/${paiement_id}/recu`,
  calendar: (month, year) => api.get('/bailleur/calendrier', { params: { month, year } }),
  subscription: () => api.get('/bailleur/subscription'),
  renewSubscription: () => api.post('/v1/abonnements/upgrade/checkout', {
    offre_code: 'loyerbook',
    moyen_paiement: 'saspay',
  }),
  paymentSync: (intent_id) => api.post('/v1/paiement/sync', { intent_id }),
};

export const locataireApi = {
  dashboard: () => api.get('/locataire/dashboard'),
  recuUrl: (paiement_id) => `${API_BASE}/locataire/paiements/${paiement_id}/recu`,
};

/** Download a PDF from an authenticated endpoint (uses fetch with bearer). */
export async function downloadPdfWithAuth(url, filename) {
  const token = getAccess();
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error('Téléchargement impossible');
  const blob = await res.blob();
  const objUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objUrl);
}

export { saveTokens, clearTokens, getAccess, getRefresh };
